import { alpha, type Components, type Theme } from '@mui/material/styles';

export function getComponents(theme: Theme): Components<Theme> {
  const isDark = theme.palette.mode === 'dark';
  const radiusCompact = theme.shape.borderRadius * 2.5;
  const radiusControl = theme.shape.borderRadius * 3;
  const radiusSurface = theme.shape.borderRadius * 4;
  const radiusShell = theme.shape.borderRadius * 4.5;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: theme.palette.background.default,
          backgroundImage: isDark
            ? 'radial-gradient(circle at top left, rgba(91,141,239,0.16), transparent 30%), radial-gradient(circle at right top, rgba(20,184,166,0.14), transparent 25%), linear-gradient(180deg, #07111F 0%, #091424 100%)'
            : 'radial-gradient(circle at top left, rgba(255,107,107,0.15), transparent 30%), radial-gradient(circle at right top, rgba(78,205,196,0.16), transparent 26%), linear-gradient(180deg, #F8FAFC 0%, #EEF4FB 100%)',
          backgroundAttachment: 'fixed',
        },
        '#root': {
          minHeight: '100vh',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.primary.main, 0.45)} transparent`,
        },
        '*::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '*::-webkit-scrollbar-thumb': {
          borderRadius: radiusCompact,
          backgroundColor: alpha(theme.palette.primary.main, 0.38),
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radiusControl,
          textTransform: 'none',
          fontWeight: 600,
          paddingInline: 18,
          minHeight: 42,
          transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&.Mui-focusVisible': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
        },
        containedPrimary: {
          color: theme.palette.primary.contrastText,
          background: theme.custom.gradients.primary,
          boxShadow: theme.shadows[4],
          '&:hover': {
            background: theme.custom.gradients.primary,
            boxShadow: theme.shadows[6],
          },
        },
        outlinedPrimary: {
          borderColor: alpha(theme.palette.primary.main, 0.28),
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: radiusSurface,
          background: theme.custom.glass.background,
          backdropFilter: theme.custom.glass.blur,
          border: `1px solid ${theme.custom.glass.border}`,
          boxShadow: theme.custom.glass.shadow,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: radiusShell,
          background: alpha(theme.palette.background.paper, isDark ? 0.78 : 0.72),
          backdropFilter: theme.custom.glass.blur,
          borderBottom: `1px solid ${theme.custom.glass.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          background: theme.custom.glass.background,
          backdropFilter: theme.custom.glass.blur,
          borderRight: `1px solid ${theme.custom.glass.border}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radiusControl,
          backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.24 : 0.72),
          transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.text.secondary, 0.18),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.4),
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radiusControl,
          fontWeight: 600,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 160ms ease, transform 160ms ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.05),
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 8,
          borderRadius: radiusSurface,
          background: alpha(theme.palette.background.paper, isDark ? 0.9 : 0.86),
          backdropFilter: theme.custom.glass.blur,
          border: `1px solid ${theme.custom.glass.border}`,
          boxShadow: theme.shadows[8],
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radiusControl,
          marginInline: 8,
          marginBlock: 2,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radiusShell,
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: radiusCompact,
          backgroundColor: alpha('#0F172A', 0.92),
          fontSize: '0.75rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
  };
}